import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// context
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// types
import { FilterTarget, IFilter, IFiltersResponse } from 'types/graphql'
// graphql
import { RISK_VIEW_FILTERS_QUERY } from 'graphql/risk/queries'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// utils
import { safeGetOr } from 'utils/fp'
import { map as mapFp, pipe } from 'lodash/fp'

type QueryResponse = {
  filters: IFiltersResponse
}

const sortRiskViewFilters = (data) =>
  data.sort((a, b) => {
    const priority = { global: 2, createdByUser: 1 }

    // Assign priority score based on global and createdByUser properties
    const scoreA = (a.args.global ? priority.global : 0) + (a.createdByUser ? priority.createdByUser : 0)
    const scoreB = (b.args.global ? priority.global : 0) + (b.createdByUser ? priority.createdByUser : 0)

    // Sort by priority score (higher score comes first)
    if (scoreA !== scoreB) {
      return scoreB - scoreA
    }

    // If priority score is same, sort by createdAt
    return a.createdAt - b.createdAt
  })

const normalizeFilters = (filtersQuery: QueryResponse, userId: string) =>
  pipe(
    safeGetOr<any, any, IFilter[]>([], 'filters.data'),
    mapFp((item: IFilter) => ({
      ...item,
      createdByUser: item.createdById === userId,
      createdAt: new Date(item.createdAt).getTime()
    })),
    sortRiskViewFilters
  )(filtersQuery)

export const useRiskViewFilters = () => {
  const user = useCurrentUser()

  const { riskView } = React.useContext(RiskRegisterContext)

  const [filters, setFilters] = React.useState<IFilter[]>([])

  const { data, refetch } = useQuery<QueryResponse>(RISK_VIEW_FILTERS_QUERY, {
    skip: !riskView,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...(riskView.isLinkedView
          ? { riskViewId: riskView?.id }
          : {
              targetId: riskView?.riskTemplate.id,
              target: FilterTarget.RiskTemplate
            })
      }
    }
  })

  React.useEffect(() => {
    const filters = normalizeFilters(data, user.id)

    setFilters(filters)
  }, [data])

  return { filters, refetch }
}
